import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import Loader from '../components/Loader';
import { getAppSettingMeta, updateAppSetting } from '../services/settingsService';
import { getTimeAgo } from '../services/walletAnalyticsService';
import '../components/AdminLayout.css';

function AdminSettings() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [commission, setCommission] = useState('');
    const [meta, setMeta] = useState({ updatedAt: null, updatedBy: null });
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const userRole = user?.user_metadata?.role || localStorage.getItem('role');
        if (String(userRole) !== '4') {
            navigate('/welcome');
            return;
        }
        loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const result = await getAppSettingMeta('referral_commission_usd');
            if (result.success && result.data) {
                setCommission(result.data.setting_value);
                setMeta({ updatedAt: result.data.updated_at, updatedBy: result.data.updatedByName });
            } else {
                setCommission('10.00');
            }
        } catch (err) {
            console.error('Failed to load settings:', err);
            setCommission('10.00');
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const parsed = parseFloat(commission);
        if (isNaN(parsed) || parsed < 0) {
            showMessage('error', 'Please enter a valid non-negative number.');
            return;
        }
        if (parsed > 1000) {
            showMessage('error', 'Commission cannot exceed $1,000.00.');
            return;
        }
        setSaving(true);
        const result = await updateAppSetting('referral_commission_usd', parsed.toFixed(2));
        setSaving(false);
        if (result.success) {
            setCommission(parsed.toFixed(2));
            showMessage('success', `Referral commission updated to $${parsed.toFixed(2)}.`);
            // Re-read the audit metadata. It is only fetched on mount, so after a
            // save the "Last changed ... by ..." line kept showing the previous
            // change — the admin who had just edited the value saw a stale date
            // and no sign their own edit had been recorded.
            const fresh = await getAppSettingMeta('referral_commission_usd');
            if (fresh.success && fresh.data) {
                setMeta({ updatedAt: fresh.data.updated_at, updatedBy: fresh.data.updatedByName });
            }
        } else {
            showMessage('error', result.error || 'Failed to update setting.');
        }
    };

    if (loading) return <AdminLayout><Loader /></AdminLayout>;

    return (
        <AdminLayout>
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <i className="bx bx-cog"></i>
                        System Settings
                    </h1>
                    <p className="page-subtitle">Configure platform-wide settings</p>
                </div>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`} style={{
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    marginBottom: '1.5rem',
                    background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
                    color: message.type === 'success' ? '#065f46' : '#991b1b',
                    border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`
                }}>
                    {message.type === 'success' ? '✅ ' : '❌ '}{message.text}
                </div>
            )}

            <div className="admin-card">
                <div className="card-header">
                    <h3 className="card-title">
                        <i className="bx bx-gift" style={{ marginRight: '0.5rem' }}></i>
                        Referral Program
                    </h3>
                </div>
                <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: '62ch', lineHeight: 1.6 }}>
                    This amount is credited to a user's USD balance when someone they referred verifies their email address. Referrals that have signed up but not yet verified show as "Awaiting Payout" on the Referrals page.
                    Changes take effect immediately for all future referral signups — bonuses already paid are not
                    recalculated.
                </p>
                <form onSubmit={handleSave}>
                    <div className="form-group">
                        <label className="form-label">Referral Commission (USD)</label>
                        <div style={{ position: 'relative', maxWidth: '280px' }}>
                            <span style={{
                                position: 'absolute', left: '0.75rem', top: '50%',
                                transform: 'translateY(-50%)', color: '#6b7280', fontWeight: 600
                            }}>$</span>
                            <input
                                type="number"
                                className="form-input"
                                style={{ paddingLeft: '1.75rem' }}
                                value={commission}
                                onChange={e => setCommission(e.target.value)}
                                min="0"
                                max="1000"
                                step="0.01"
                                required
                            />
                        </div>
                        <p style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                            Minimum $0.00 · Maximum $1,000.00
                        </p>
                    </div>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={saving}
                        style={{ marginTop: '0.5rem' }}
                    >
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </form>

                {meta.updatedAt && (
                    <p style={{ fontSize: '0.8rem', color: '#a1a5b7', marginTop: '1.5rem', marginBottom: 0 }}>
                        <i className="bx bx-history" style={{ marginRight: '0.35rem', verticalAlign: '-2px' }}></i>
                        Last changed {getTimeAgo(meta.updatedAt)}
                        {meta.updatedBy ? ` by ${meta.updatedBy}` : ''}
                        <span title={new Date(meta.updatedAt).toLocaleString()}>
                            {' '}({new Date(meta.updatedAt).toLocaleDateString()})
                        </span>
                    </p>
                )}
            </div>
        </AdminLayout>
    );
}

export default AdminSettings;
